import { TCoverageSideEffectsType } from '@sureapp/platform-sdk';
import Text from '@sureapp/wl-toggle-common/dist/components/Text';
import produce from 'immer';

import { TConfig, TConfigInput, TConfigParams } from '@@/auto/interfaces';

import classes from '../../../components/CoverageSections/CoverageSectionDropdown/CoverageSectionDropdown.module.css';
import { AUTO_DISCOUNTS } from '../../autoDiscounts';
import { featureFlag } from '../../featureFlags';

function TogglePAConfig({
  isFeatureEnabled,
}: TConfigParams): DeepPartial<TConfigInput> {
  const stackedModalContent = (
    <>
      The amount of insurance coverage available can be impacted based on your
      stacking option selection.
      <br />
      <br />
      <Text
        variant="inherit"
        component="strong"
        className={classes.text_strong_purple}
      >
        Unstacked
      </Text>{' '}
      - The coverage benefit is up to the individual stated coverage limit for
      the insured vehicle covered on the policy.
      <br />
      <br />
      <Text
        variant="inherit"
        component="strong"
        className={classes.text_strong_purple}
      >
        Stacked
      </Text>{' '}
      - The coverage benefit is up to the sum of the stated coverage limit for
      each insured vehicle covered on the policy.
    </>
  );

  return isFeatureEnabled(featureFlag.IS_PA_ENABLED)
    ? {
        antiTheftCopyConfig: () => ({
          label: 'Anti-theft protection',
          explanationText:
            'Is your vehicle equipped with a passive anti-theft device?',
        }),
        autoDiscounts: [
          AUTO_DISCOUNTS.distantStudent,
          AUTO_DISCOUNTS.goodStudent,
          AUTO_DISCOUNTS.homeowner,
          AUTO_DISCOUNTS.affinity,
          AUTO_DISCOUNTS.driverImprovementDiscount,
          AUTO_DISCOUNTS.antiTheftPa,
          AUTO_DISCOUNTS.passiveRestraint,
          AUTO_DISCOUNTS.safeDriving,
          AUTO_DISCOUNTS.multiCar,
          AUTO_DISCOUNTS.multiPolicy,
          AUTO_DISCOUNTS.homeowner,
          AUTO_DISCOUNTS.goodStudent,
          AUTO_DISCOUNTS.distantStudent,
          AUTO_DISCOUNTS.teenDriver,
        ],
        matureDriver: () => ({
          getSubjectText: null,
          label:
            'Has this person completed a motor vehicle driver improvement course within the last 3 years?',
          explanation:
            'Approved driver improvement courses help improve driving skills and reduce driving risks by anticipating situations and making safe well-informed decisions. Courses required because of a driving offense or traffic violation do not apply.',
        }),
        coverage: (coverage: TConfig['coverage']) =>
          produce(coverage, (draft) => {
            draft.medical = {
              title: 'Medical Benefits',
              description: `After an accident, you want to focus on what the doctor is saying, not worrying about what it'll cost you. This coverage can help with those medical bills.`,
              modalContent:
                'Coverage pays reasonable medical expenses incurred by an insured person on your policy caused by an accident, up to the limit selected and regardless of fault.',
            };

            draft.accidentalDeathLimit = {
              title: 'Accidental Death Benefits',
              description: `The loss of a loved one is tough enough. This coverage can help provide compensation if the unimaginable happens.`,
              modalContent: `Coverage can be paid to the insured's family or designated personal representative if an insured's death is the result of an accident related injury and within 24 months from the date of the accident, up to the limit selected.`,
            };

            draft.extraordinaryMedical = {
              title: 'Extraordinary Medical Benefits',
              description:
                ' is available since you selected $100,000 in Medical Benefits coverage.',
              modalContent:
                'Coverage is an excess coverage that pays reasonable medical expenses incurred by an insured person on your policy caused by an accident, up to the coverage limit and regardless of fault, in addition to your Medical Benefits coverage limit.',
              textAlertContent:
                'Extraordinary medical benefits is optional when you select $100,000 in Medical Benefits coverage.',
            };
            draft.funeralBenefitLimit = {
              title: 'Funeral Benefits',
              description: `If the unimaginable happens, this coverage can help you focus on remembering your loved ones, not worrying about the costs.`,
              modalContent: `Coverage can pay reasonable expenses related to the funeral, burial or cremation should an insured's death result from an accident within 24 months of a covered loss, up to the limit selected.`,
            };

            draft.incomeLossLimit = {
              title: 'Income Loss Benefits',
              description: `An accident becomes more than a rough day if you or someone you love can't return to work as a direct result of injuries. If that happens, this coverage can help until you're back on your feet.`,
              modalContent:
                'Coverage can pay for lost wages while you are unable to return to work due to injuries directly sustained in an automobile accident, up to the coverage limits selected.',
              caption: `per month / per maximum`,
            };

            draft.packagedCoverages = {
              title: 'First party benefits combination package',
              description:
                'Accidents can be scary, especially when you or someone you love is injured. If that happens, though, this combination of coverages can help lessen the impact to your wallet (or theirs).',
              modalContent:
                'This coverage groups your selection of available coverages and pays up to the limit listed when there is an automobile accident and qualifying coverage condition(s):  Medical Benefits $100,000, Income Loss (per mo/max) $2,500/$50,000, Accidental Death $25,000, and Funeral Benefits $2,500.',
            };

            // UIMST
            draft.underinsuredMotoristStacked = {
              title: 'Underinsured Motorist Bodily Injury Stacking Option',
              description: `: If you selected underinsured motorist coverage, your next
                  decision is to decide if you wish to stack the limits of this
                  coverage.`,
              modalContent: stackedModalContent,
            };

            // UMST
            draft.uninsuredMotoristStacked = {
              title: 'Uninsured Motorist Bodily Injury Stacking Option',
              description: `: If you selected uninsured motorist coverage, your next
                  decision is to decide if you wish to stack the limits of this
                  coverage.`,
              modalContent: stackedModalContent,
            };

            draft.fullTort = {
              title: 'Tort Option',
              description: `Sure, it's a relief when the accident wasn't your fault -- but that relief isn't making your injuries feel any better. Depending on the option selected, this coverage can help you recover your expenses or get financial compensation if you're injured in an accident caused by someone else.`,
              modalContent: (
                <>
                  The laws of the Commonwealth of Pennsylvania give you the
                  right to choose either of the following two tort options:
                  <br />
                  <br />
                  <Text
                    variant="inherit"
                    component="strong"
                    className={classes.text_strong_purple}
                  >
                    &quot;Limited Tort&quot;
                  </Text>{' '}
                  Option - This form of insurance limits your right and the
                  rights of members of your household to seek financial
                  compensation for injuries caused by other drivers. Under this
                  form of insurance, you and other household members covered
                  under an issued policy may seek recovery for all medical and
                  other out-of-pocket expenses, but not for pain and suffering
                  or other nonmonetary damages unless the injuries suffered fall
                  within the definition of &quot;serious injury,&quot; as set
                  forth in the policy, or unless one of several other exceptions
                  noted in the policy applies.
                  <br />
                  <br />
                  <Text
                    variant="inherit"
                    component="strong"
                    className={classes.text_strong_purple}
                  >
                    &quot;Full Tort&quot;
                  </Text>{' '}
                  Option - This form of insurance allows you to maintain an
                  unrestricted right for yourself and other members of your
                  household to seek financial compensation for injuries caused
                  by other drivers. Under this form of insurance, you and other
                  household members covered under an issued policy may seek
                  recovery for all medical and other out-of-pocket expenses and
                  may also seek financial compensation for pain and suffering or
                  other nonmonetary damages as a result of injuries caused by
                  other drivers.
                </>
              ),
            };
          }),
        visibleCoverages: (visibleCoverages: TConfig['visibleCoverages']) => {
          return {
            ...visibleCoverages,
            protectionCoverages: [
              ...visibleCoverages.protectionCoverages.filter(
                ({ key }) =>
                  ![
                    'pet_passenger',
                    'full_tort',
                    'medical_limit',
                    'extraordinary_medical',
                    'funeral_benefit_limit',
                    'accidental_death_limit',
                    'underinsured_motorist_bodily_injury_limit',
                    'uninsured_motorist_bodily_injury_limit',
                    'income_loss_limit',
                  ].includes(key)
              ),
              {
                label: 'Tort Option',
                key: 'full_tort',
                overrideValueLabel: '',
                isOverrideNoCoverageClass: true,
              },
              {
                label: 'Medical Benefits',
                key: 'medical_limit',
              },

              {
                label: 'Extraordinary Medical Benefits',
                key: 'extraordinary_medical',
                overrideValueLabel: '',
              },
              {
                label: 'Income loss benefits',
                key: 'income_loss_limit',
              },
              {
                label: 'Accidental Death Benefits',
                key: 'accidental_death_limit',
              },
              {
                label: 'Funeral Benefits',
                key: 'funeral_benefit_limit',
              },
              {
                label: 'Uninsured Motorist Bodily Injury',
                key: 'uninsured_motorist_bodily_injury_limit',
                hasSubLabel: true,
              },
              {
                label: 'Underinsured Motorist Bodily Injury',
                key: 'underinsured_motorist_bodily_injury_limit',
                hasSubLabel: true,
              },
              visibleCoverages.protectionCoverages.find(
                ({ key }) => key === 'pet_passenger'
              ),
            ],
            vehicleCoverages: [
              ...visibleCoverages.vehicleCoverages.filter(
                ({ key }) =>
                  !['uninsured_motorist_property_damage_limit'].includes(key)
              ),
            ],
          };
        },
        coverageTextAlerts: (
          coverageTextAlerts: TConfig['coverageTextAlerts']
        ) => ({
          ...coverageTextAlerts,
          UIMBI_UPDATE: ({
            isIncreased,
            causeName,
          }: TCoverageSideEffectsType) => {
            if (!isIncreased) {
              return `We've decreased your Underinsured Motorist Bodily Injury limits to match the changes you've made to your ${causeName} coverage.`;
            }
          },
        }),
        vehicleSideEffectMessages: (
          vehicleSideEffectMessages: TConfig['vehicleSideEffectMessages']
        ) =>
          produce(vehicleSideEffectMessages, (draft) => {
            draft.gap =
              'This coverage is only available if Comprehensive and Collision are also selected, the vehicle is financed or leased, and the model year is four (4) years old or less.';
            draft.new_car_replacement = `This coverage is only available with a model year that's 2 years old or less.`;
            draft.oem_parts = `This coverage is only available with a model year that's 10 years old or less.`;
            draft.towing_road_services = `This coverage is only available if Comprehensive and Collision are also selected.`;
            draft.temporary_ride = `This coverage is only available if Comprehensive and Collision are also selected.`;
          }),
        initialCoverageValues: (
          initialCoverageValues: TConfig['initialCoverageValues']
        ) =>
          produce(initialCoverageValues, (draft) => {
            draft.gap = 'false';
            draft.temporary_ride = 'null';
            draft.side_hustle = 'false';
            draft.extraordinary_medical = 'false';
          }),
      }
    : {};
}
export default TogglePAConfig;
